import { useState, useEffect, useRef } from "react";
import { useSignUp, useClerk, useAuth } from "@clerk/react";

const API_URL = process.env.REACT_APP_API_URL || "";

const STYLES = `
  .ai-page {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
    background-color: #ffffff;
    background-image: url('./dotted-earth.png');
    background-size: 96vmin 96vmin;
    background-position: center center;
    background-repeat: no-repeat;
    position: relative;
    display: flex;
    flex-direction: column;
    margin: 0; padding: 0;
  }
  .ai-logo-bar {
    position: absolute;
    top: 32px;
    left: 40px;
    z-index: 10;
  }
  .ai-logo-img {
    height: 45px;
    width: auto;
    display: block;
  }
  .ai-center {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 5;
    padding: 120px 24px 20px;
  }
  .ai-panel {
    background: #ffffff;
    border-radius: 18px;
    box-shadow: 0 8px 40px rgba(15,33,78,0.13), 0 1px 4px rgba(0,0,0,0.05);
    padding: 40px 44px;
    width: 100%;
    max-width: 420px;
    box-sizing: border-box;
  }
  .ai-copyright {
    text-align: center;
    padding: 18px 24px;
    font-size: 13px;
    color: #94a3b8;
    position: relative;
    z-index: 5;
    font-family: 'Inter', sans-serif;
  }
  .ai-heading {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.4px;
    margin: 0 0 6px;
    font-family: 'Inter', sans-serif;
  }
  .ai-subhead {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 28px;
    line-height: 1.5;
    font-family: 'Inter', sans-serif;
  }
  .ai-form { display: flex; flex-direction: column; }
  .ai-fields { display: flex; flex-direction: column; gap: 18px; }
  .ai-name-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .ai-field-group { display: flex; flex-direction: column; }
  .ai-label {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    display: block;
    margin-bottom: 7px;
    font-family: 'Inter', sans-serif;
  }
  .ai-input {
    background: #ffffff;
    color: #0f172a;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    height: 48px;
    padding: 0 16px;
    font-size: 15px;
    font-family: 'Inter', sans-serif;
    box-sizing: border-box;
    width: 100%;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .ai-input:focus {
    border-color: #232231;
    box-shadow: 0 0 0 3px rgba(35,34,49,0.1);
  }
  .ai-input::placeholder { color: #94a3b8; }
  .ai-btn {
    background: #232231;
    color: #fff;
    border: none;
    border-radius: 10px;
    height: 48px;
    padding: 0 24px;
    font-weight: 600;
    font-size: 15px;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    transition: background 0.15s;
    letter-spacing: -0.1px;
    text-decoration: none;
    box-sizing: border-box;
  }
  .ai-btn:hover:not(:disabled) { background: #1a1926; }
  .ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ai-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 10px 14px;
    color: #dc2626;
    font-size: 13px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
  }
  @media (max-width: 600px) {
    .ai-logo-bar { top: 20px; left: 20px; }
    .ai-logo-img { height: 36px; }
    .ai-panel { padding: 32px 24px; border-radius: 14px; }
    .ai-center { padding-top: 100px; }
    .ai-name-row { grid-template-columns: 1fr; }
  }
`;

if (!document.querySelector("[data-ai]")) {
  const _s = document.createElement("style");
  _s.setAttribute("data-ai", "");
  _s.textContent = STYLES;
  document.head.appendChild(_s);
}

export default function AcceptInvitation() {
  const { signUp }    = useSignUp();
  const { setActive } = useClerk();
  const { getToken }  = useAuth();
  const processed     = useRef(false);

  // Capture ticket before Clerk strips it from the URL
  const [ticket] = useState(() => new URLSearchParams(window.location.search).get("__clerk_ticket"));

  const [step,      setStep]      = useState("loading");
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [password,  setPassword]  = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error,     setError]     = useState("");
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    return () => {
      try {
        const el = document.querySelector("[data-ai]");
        if (el) document.head.removeChild(el);
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    if (!signUp || processed.current) return;
    processed.current = true;

    if (!ticket) {
      setStep("error");
      setError("No invitation token found. Please use the link from your invitation email.");
      return;
    }

    const fn = signUp.firstName || signUp.unsafeMetadata?.first_name || signUp.publicMetadata?.first_name || "";
    const ln = signUp.lastName  || signUp.unsafeMetadata?.last_name  || signUp.publicMetadata?.last_name  || "";
    if (fn) setFirstName(fn);
    if (ln) setLastName(ln);

    setStep("set-password");
  }, [signUp, ticket]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) { setError("Passwords do not match."); return; }
    if (password.length < 8)         { setError("Password must be at least 8 characters."); return; }
    if (!/[0-9]/.test(password))     { setError("Password must include at least one number (0–9)."); return; }
    if (!/[^a-zA-Z0-9]/.test(password)) { setError("Password must include at least one special character (e.g. !@#$%)."); return; }
    setError("");
    setLoading(true);

    try {
      await signUp.create({
        strategy:      "ticket",
        ticket,
        password,
        firstName:     firstName.trim() || undefined,
        lastName:      lastName.trim()  || undefined,
        legalAccepted: true,
      });

      if (signUp.status === "complete" && signUp.createdSessionId) {
        window.history.replaceState(null, "", "/accept-invitation");
        await setActive({ session: signUp.createdSessionId });

        // Ensure the user is in D1 before navigating — getToken() can return null
        // immediately after setActive(), so retry a few times with a short delay.
        try {
          let token = null;
          for (let i = 0; i < 5 && !token; i++) {
            token = await getToken();
            if (!token) await new Promise(r => setTimeout(r, 400));
          }
          if (token) {
            await fetch(`${API_URL}/api/registerUser`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            });
          }
        } catch (_) {}

        window.location.href = "/";
        return;
      }

      if (signUp.status === "missing_requirements") {
        // Clerk sometimes swallows 422s — surface the real error if available
        const inlineErr = signUp.errors?.[0];
        if (inlineErr) {
          setError(inlineErr.longMessage || inlineErr.message || "Could not complete account setup.");
          return;
        }
        const missing = signUp.missingFields || [];
        if (missing.includes("email_address")) {
          setError("Please verify your email address — check your inbox for a verification email.");
        } else {
          setError("Could not complete account setup. Please try again or contact support.");
        }
      } else {
        setError("Could not complete account setup. Please try again or contact support.");
      }
    } catch (err) {
      const clerkErr = err?.errors?.[0];
      const code     = clerkErr?.code || "";
      const msg      = (clerkErr?.message || "").toLowerCase();

      if (
        code === "form_identifier_exists" ||
        code === "resource_already_exists" ||
        code === "ticket_invalid" ||
        code === "invitation_already_accepted" ||
        msg.includes("already") ||
        msg.includes("taken")
      ) {
        setStep("already-exists");
      } else {
        setError(
          clerkErr?.longMessage ||
          clerkErr?.message ||
          "Failed to create account. Please try again or contact support."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-page">

      <div className="ai-logo-bar">
        <img src="./logo-blue.png" alt="GeoSpekter" className="ai-logo-img" />
      </div>

      <div className="ai-center">
        <div className="ai-panel">

          {step === "loading" && (
            <p style={{ margin: 0, fontSize: 14, color: "#64748b", fontFamily: "Inter, sans-serif" }}>
              Verifying your invitation…
            </p>
          )}

          {step === "error" && (
            <>
              <h1 className="ai-heading">Invitation error</h1>
              <p className="ai-subhead">{error}</p>
              <a href="/" className="ai-btn">Back to sign in</a>
            </>
          )}

          {step === "already-exists" && (
            <>
              <h1 className="ai-heading">Account already exists</h1>
              <p className="ai-subhead">
                An account for this email has already been created. You can sign in directly.
              </p>
              <a href="/" className="ai-btn">Sign in</a>
            </>
          )}

          {step === "set-password" && (
            <form className="ai-form" onSubmit={handleSetPassword}>
              <h1 className="ai-heading">
                {firstName ? `Hi ${firstName},` : "You're invited"}
              </h1>
              <p className="ai-subhead">Set a password to complete your account setup.</p>

              <div className="ai-fields">
                <div className="ai-name-row">
                  <div className="ai-field-group">
                    <label className="ai-label">First name</label>
                    <input type="text" required autoFocus className="ai-input"
                      value={firstName} onChange={e => setFirstName(e.target.value)}
                      autoComplete="given-name" />
                  </div>
                  <div className="ai-field-group">
                    <label className="ai-label">Last name</label>
                    <input type="text" required className="ai-input"
                      value={lastName} onChange={e => setLastName(e.target.value)}
                      autoComplete="family-name" />
                  </div>
                </div>

                <div className="ai-field-group">
                  <label className="ai-label">Password</label>
                  <input type="password" minLength="8" required className="ai-input"
                    value={password} onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password" placeholder="Min. 8 characters" />
                  <p style={{ margin: "6px 0 0", fontSize: 12, color: "#94a3b8", fontFamily: "Inter, sans-serif" }}>
                    Must include a number and a special character (e.g. !@#$%).
                  </p>
                </div>

                <div className="ai-field-group">
                  <label className="ai-label">Confirm password</label>
                  <input type="password" minLength="8" required className="ai-input"
                    value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                    autoComplete="new-password" />
                </div>

                <div id="clerk-captcha" />

                {error && <div className="ai-error">{error}</div>}

                <button type="submit" className="ai-btn" disabled={loading}>
                  {loading ? "Please wait…" : "Create account"}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>

      <div className="ai-copyright">
        © {new Date().getFullYear()} GeoSpekter. All Rights Reserved.
      </div>

    </div>
  );
}
